import type { Payment } from "./model.ts";

/** The old FMS-Control price and the date the grace period for it ends (FMS staff set both). */
export interface LegacyPrice {
  /** A month, e.g. 30. */
  price: number;
  until: number;
}

/** "On the FMS-Control price ($30 a month) until 29 Apr 2027", or that it has ended. */
export function legacyText(legacy: LegacyPrice | undefined, now: number): string | undefined {
  if (!legacy) return undefined;
  const date = new Date(legacy.until).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  return legacy.until > now
    ? `On the FMS-Control price ($${legacy.price} a month) until ${date}`
    : `The FMS-Control price ($${legacy.price} a month) ended on ${date}`;
}

/**
 * Subscriptions that are still paying (or about to): while one is, a new one isn't needed.
 * Not "No payment lately": an older Subscribe-button subscription that has stopped must
 * leave the farm free to start a new one (it can't be cancelled or seen any other way).
 */
export const isPaying = (p: Pick<Payment, "status">) => !["CANCELLED", "EXPIRED", "APPROVAL_PENDING", "NO_RECENT_PAYMENT"].includes(p.status);

/** Something FMS should look at: not active, payments failing, or a payment overdue. */
export function paymentProblem(p: Payment | undefined, now: number): string | undefined {
  if (!p) return undefined;
  if (p.status === "SUSPENDED") return "PayPal has paused this subscription (usually payments failing)";
  if (p.status === "CANCELLED") return "The PayPal subscription was cancelled";
  if (p.status === "EXPIRED") return "The PayPal subscription has ended";
  if (p.status === "APPROVAL_PENDING") return "The customer hasn't finished approving the subscription in PayPal";
  if (p.status === "NO_RECENT_PAYMENT") return "No PayPal payment from this subscription in over 40 days";
  if (p.failedPayments > 0) return `${p.failedPayments} PayPal payment${p.failedPayments === 1 ? "" : "s"} failed`;
  if (p.nextBillingAt !== undefined && p.nextBillingAt < now - 3 * 86_400_000) return "A PayPal payment is overdue";
  return undefined;
}
